import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  CheckCircle, 
  Circle, 
  ArrowLeft,
  BookOpen,
  Clock,
  Target,
  Trophy,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  PlayCircle,
  FileText,
  Video,
  Image,
  Music,
  Link,
  Package
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { InteractivePoll, InteractiveCalculator, InteractiveWordCloud, InteractiveQuiz } from './InteractiveElements';
import { ContentResponseEditor } from './ContentResponseEditor';
import ReactMarkdown from 'react-markdown';

interface Course {
  id: string;
  title: string;
  description: string;
  status: string;
  executive_summary?: string;
  target_educator_description?: string;
  learning_objectives?: string[];
  learning_outcomes?: string[];
}

interface CourseModule {
  id: string;
  course_id: string;
  title: string;
  description?: string;
  module_order: number;
  duration_minutes?: number;
  is_required: boolean;
}

interface CourseContent {
  id: string;
  module_id: string;
  title: string;
  content_type: string;
  content_url?: string;
  content_text?: string;
  youtube_url?: string;
  vimeo_url?: string;
  duration_minutes?: number;
  content_order: number;
  metadata?: any;
}

interface NBCTStyleCoursePlayerProps {
  courseId: string;
}

export const NBCTStyleCoursePlayer: React.FC<NBCTStyleCoursePlayerProps> = ({ courseId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [course, setCourse] = useState<Course | null>(null);
  const [modules, setModules] = useState<CourseModule[]>([]);
  const [moduleContent, setModuleContent] = useState<{ [key: string]: CourseContent[] }>({});
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [completedContent, setCompletedContent] = useState<Set<string>>(new Set());
  const [reflections, setReflections] = useState<{[key: string]: string}>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (courseId) {
      loadCourseData();
    }
  }, [courseId]);

  const loadCourseData = async () => {
    setLoading(true);
    try {
      // Fetch course data
      const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .select('*')
        .eq('id', courseId)
        .single();

      if (courseError) throw courseError;
      setCourse(courseData);

      // Fetch modules
      const { data: modulesData, error: modulesError } = await supabase
        .from('course_modules')
        .select('*')
        .eq('course_id', courseId)
        .order('module_order', { ascending: true });

      if (modulesError) throw modulesError;
      setModules(modulesData || []);

      // Fetch content for all modules
      const contentPromises = (modulesData || []).map(module =>
        supabase
          .from('course_content')
          .select('*')
          .eq('module_id', module.id)
          .order('content_order', { ascending: true })
      );

      const contentResults = await Promise.all(contentPromises);
      const contentMap: { [key: string]: CourseContent[] } = {};
      
      contentResults.forEach((result, index) => {
        if (!result.error && result.data) {
          contentMap[modulesData[index].id] = result.data;
        }
      });
      
      setModuleContent(contentMap);

    } catch (error) {
      console.error("Error loading course data:", error);
      toast({
        title: "Error",
        description: "Failed to load course data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleContentCompletion = (contentId: string) => {
    setCompletedContent(prev => new Set([...prev, contentId]));
    toast({
      title: "Progress Saved",
      description: "Content marked as completed"
    });
  };

  const handleReflectionChange = (key: string, value: string) => {
    setReflections(prev => ({ ...prev, [key]: value }));
  };

  const getTotalProgress = () => {
    const totalModules = modules.length;
    const completedModules = modules.filter(module => {
      const content = moduleContent[module.id] || [];
      return content.length > 0 && content.every(c => completedContent.has(c.id));
    }).length;
    return totalModules > 0 ? (completedModules / totalModules) * 100 : 0;
  };

  const getContentIcon = (contentType: string) => {
    switch (contentType) {
      case 'rich_text': return <FileText className="h-4 w-4" />;
      case 'text': return <FileText className="h-4 w-4" />;
      case 'video': return <Video className="h-4 w-4" />;
      case 'audio': return <Music className="h-4 w-4" />;
      case 'image': return <Image className="h-4 w-4" />;
      case 'presentation': return <FileText className="h-4 w-4" />;
      case 'document': return <FileText className="h-4 w-4" />;
      case 'url': return <Link className="h-4 w-4" />;
      case 'scorm': return <Package className="h-4 w-4" />;
      case 'interactive': return <Trophy className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const renderContent = (content: CourseContent) => {
    try {
      switch (content.content_type) {
        case 'rich_text':
        case 'text':
          const textContent = content.content_text || '';
          // Check if content is HTML or plain text/markdown
          const isHTML = /<[^>]+>/.test(textContent);
          
          return (
            <div className="space-y-4">
              <div className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-bold prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl prose-p:mb-4 prose-ul:my-4 prose-li:mb-2">
                {isHTML ? (
                  <div dangerouslySetInnerHTML={{ __html: textContent }} />
                ) : (
                  <ReactMarkdown
                    components={{
                      h1: ({node, ...props}) => <h1 className="text-3xl font-bold mb-4 mt-6" {...props} />,
                      h2: ({node, ...props}) => <h2 className="text-2xl font-bold mb-3 mt-5" {...props} />,
                      h3: ({node, ...props}) => <h3 className="text-xl font-bold mb-2 mt-4" {...props} />,
                      h4: ({node, ...props}) => <h4 className="text-lg font-bold mb-2 mt-3" {...props} />,
                      p: ({node, ...props}) => <p className="mb-4 leading-relaxed whitespace-pre-wrap" {...props} />,
                      ul: ({node, ...props}) => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />,
                      ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />,
                      li: ({node, ...props}) => <li className="mb-2" {...props} />,
                      blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-primary pl-4 italic my-4" {...props} />,
                      strong: ({node, ...props}) => <strong className="font-bold text-foreground" {...props} />,
                      em: ({node, ...props}) => <em className="italic" {...props} />,
                      code: ({node, inline, ...props}: any) => 
                        inline ? (
                          <code className="bg-muted px-1 py-0.5 rounded text-sm" {...props} />
                        ) : (
                          <code className="block bg-muted p-4 rounded-lg text-sm my-4 overflow-x-auto" {...props} />
                        ),
                      br: () => <br className="my-2" />,
                    }}
                  >
                    {textContent}
                  </ReactMarkdown>
                )}
              </div>
              <ContentResponseEditor 
                contentId={content.id}
                contentTitle={content.title}
                contentType={content.content_type}
              />
            </div>
          );

        case 'interactive':
          try {
            const metadata = content.metadata as any;
            const interactiveType = metadata?.interactiveType;
            
            console.log('Interactive element:', { 
              contentId: content.id, 
              type: interactiveType, 
              metadata,
              contentText: content.content_text?.substring(0, 100)
            });
            
            let config: any = {};
            
            // Try to parse config from content_text
            if (content.content_text) {
              try {
                config = JSON.parse(content.content_text);
              } catch (parseError) {
                console.error('Failed to parse content_text as JSON:', parseError);
                // If it's not JSON, treat it as plain text description
                config = { description: content.content_text };
              }
            }

            // Validate we have a valid interactive type
            if (!interactiveType) {
              console.warn('No interactive type specified for content:', content.id);
              // Skip rendering this content if not properly configured
              return null;
            }

            const interactiveElement = (() => {
              switch (interactiveType) {
                case 'poll':
                  return (
                    <InteractivePoll
                      contentId={content.id}
                      question={config.question || 'Poll Question'}
                      options={config.options || ['Option 1', 'Option 2']}
                    />
                  );
                case 'calculator':
                  return (
                    <InteractiveCalculator
                      title={config.title || 'Calculator'}
                      inputs={config.inputs || []}
                      formula={config.formula || 'return 0;'}
                      resultLabel={config.resultLabel || 'Result'}
                    />
                  );
                case 'wordcloud':
                  return (
                    <InteractiveWordCloud
                      contentId={content.id}
                      prompt={config.prompt || 'Share your thoughts'}
                    />
                  );
                case 'quiz':
                  const questions = config.questions || [{
                    question: config.question || 'Quiz Question',
                    options: config.options || ['Answer 1', 'Answer 2'],
                    correctAnswer: config.correctAnswer || 0
                  }];
                  return (
                    <InteractiveQuiz
                      contentId={content.id}
                      questions={questions}
                    />
                  );
                default:
                  return (
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        Interactive element type "{interactiveType}" not yet supported.
                      </p>
                    </div>
                  );
              }
            })();

            return (
              <div className="space-y-4">
                {interactiveElement}
                <ContentResponseEditor 
                  contentId={content.id}
                  contentTitle={content.title}
                  contentType={content.content_type}
                  prompt={config.prompt || config.question || "Complete the activity above and share your reflections."}
                />
              </div>
            );
          } catch (error) {
            console.error('Error rendering interactive content:', error, {
              contentId: content.id,
              metadata: content.metadata,
              contentText: content.content_text
            });
            return (
              <div className="space-y-4">
                <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm text-destructive font-medium">
                    Unable to load interactive element
                  </p>
                  <p className="text-xs text-destructive/80 mt-1">
                    Error: {error instanceof Error ? error.message : 'Unknown error'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    The course administrator has been notified. You can continue with other content.
                  </p>
                </div>
                <ContentResponseEditor 
                  contentId={content.id}
                  contentTitle={content.title}
                  contentType={content.content_type}
                />
              </div>
            );
          }

      case 'video':
        const videoElement = (() => {
          // Handle YouTube videos
          if (content.youtube_url) {
            const getYouTubeEmbedUrl = (url: string) => {
              const videoIdMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
              const videoId = videoIdMatch ? videoIdMatch[1] : null;
              return videoId ? `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1` : null;
            };
            
            const embedUrl = getYouTubeEmbedUrl(content.youtube_url);
            if (embedUrl) {
              return (
                <div className="aspect-video w-full">
                  <iframe
                    src={embedUrl}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    title={content.title}
                    className="w-full h-full rounded-lg"
                  />
                </div>
              );
            }
          }

          // Handle video files and other video URLs
          if (content.content_url) {
            const url = content.content_url.toLowerCase();
            const videoExtensions = ['mp4', 'webm', 'ogg', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'm4v', '3gp'];
            const fileExtension = content.content_url.split('.').pop()?.toLowerCase();
            
            if (videoExtensions.includes(fileExtension || '') || url.includes('video')) {
              return (
                <div className="aspect-video w-full">
                  <video 
                    controls 
                    className="w-full h-full rounded-lg bg-black"
                    preload="metadata"
                  >
                    <source src={content.content_url} type={`video/${fileExtension || 'mp4'}`} />
                    Your browser does not support the video tag.
                  </video>
                </div>
              );
            }
          }
          return null;
        })();

        return (
          <div className="space-y-4">
            {videoElement}
            <ContentResponseEditor 
              contentId={content.id}
              contentTitle={content.title}
              contentType={content.content_type}
              prompt="After viewing the video, share your key takeaways and how you plan to apply what you learned."
            />
          </div>
        );

      case 'document':
      case 'presentation':
        if (content.content_url) {
          const fileExtension = content.content_url.split('.').pop()?.toLowerCase();
          
          // Handle PDF documents
          if (fileExtension === 'pdf') {
            return (
              <div className="aspect-[4/3] w-full">
                <iframe
                  src={content.content_url}
                  title={content.title}
                  className="w-full h-full rounded-lg border"
                />
              </div>
            );
          }
          
          // Handle Office documents
          if (['ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx'].includes(fileExtension || '')) {
            const encodedUrl = encodeURIComponent(content.content_url);
            return (
              <div className="aspect-[4/3] w-full">
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`}
                  title={content.title}
                  className="w-full h-full rounded-lg border"
                  frameBorder="0"
                />
              </div>
            );
          }
        }
        break;

      case 'url':
        if (content.content_url) {
          return (
            <div className="aspect-video w-full">
              <iframe
                src={content.content_url}
                title={content.title}
                className="w-full h-full rounded-lg border"
              />
            </div>
          );
        }
        break;

      default:
        return (
          <div className="text-center py-12">
            <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground">Content not available</h3>
            <p className="text-sm text-muted-foreground">This content type is not supported yet.</p>
          </div>
        );
    }

    return (
      <div className="text-center py-12">
        <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-muted-foreground">No content available</h3>
      </div>
    );
  } catch (error) {
    console.error('Error rendering content:', error);
    return (
      <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
        <p className="text-sm text-destructive">
          Error loading content. Please try refreshing the page.
        </p>
      </div>
    );
  }
};

  if (loading) {
    return <div className="p-6">Loading course...</div>;
  }

  if (!course) {
    return <div className="p-6">Course not found</div>;
  }

  const progress = getTotalProgress();
  const totalModules = modules.length;
  const completedModules = modules.filter(module => {
    const content = moduleContent[module.id] || [];
    return content.length > 0 && content.every(c => completedContent.has(c.id));
  }).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
      <div className="flex items-center space-x-4">
        <div className="p-3 rounded-lg bg-primary text-primary-foreground">
          <BookOpen className="h-8 w-8" />
        </div>
        <div>
          <h2 className="text-2xl font-bold">{course.title}</h2>
          <p className="text-muted-foreground">{course.description}</p>
        </div>
      </div>
      </div>

      {/* Progress Card */}
      <Card>
        <CardHeader>
          <CardTitle>Your Progress</CardTitle>
          <CardDescription>
            {completedModules} of {totalModules} modules completed
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Progress value={progress} className="h-3" />
          <div className="flex justify-between text-sm text-muted-foreground mt-2">
            <span>{progress.toFixed(0)}% Complete</span>
            <span>{modules.length} Module{modules.length !== 1 ? 's' : ''}</span>
          </div>
        </CardContent>
      </Card>

      {/* Executive Summary */}
      {course.executive_summary && (
        <Card>
          <CardHeader>
            <CardTitle>Executive Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{course.executive_summary}</p>
          </CardContent>
        </Card>
      )}

      {/* Target Educator */}
      {course.target_educator_description && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              Who Should Take This Course
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{course.target_educator_description}</p>
          </CardContent>
        </Card>
      )}

      {/* Learning Objectives */}
      {course.learning_objectives && course.learning_objectives.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              Learning Objectives
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {course.learning_objectives.map((objective, index) => (
                <li key={index} className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{objective}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Learning Outcomes */}
      {course.learning_outcomes && course.learning_outcomes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Expected Outcomes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {course.learning_outcomes.map((outcome, index) => (
                <li key={index} className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{outcome}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Course Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Course Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <Badge variant="secondary">Professional Development</Badge>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Self-Paced Learning</span>
            </div>
            <div className="flex items-center space-x-2">
              <Target className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Interactive Content</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Course Modules */}
      <Card>
        <CardHeader>
          <CardTitle>Learning Modules</CardTitle>
          <CardDescription>Follow these modules to complete your learning journey</CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible value={`module-${currentModuleIndex}`}>
            {modules.map((module, moduleIndex) => {
              const content = moduleContent[module.id] || [];
              const moduleCompleted = content.every(c => completedContent.has(c.id));
              const moduleProgress = content.length > 0 ? (content.filter(c => completedContent.has(c.id)).length / content.length) * 100 : 0;
              
              return (
                <AccordionItem key={module.id} value={`module-${moduleIndex}`}>
                  <AccordionTrigger 
                    className="hover:no-underline"
                    onClick={() => setCurrentModuleIndex(moduleIndex)}
                  >
                    <div className="flex items-center space-x-3">
                      {moduleCompleted ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <Circle className="h-5 w-5 text-muted-foreground" />
                      )}
                      <div className="text-left">
                        <div className="font-medium">Module {moduleIndex + 1}: {module.title}</div>
                        <div className="text-sm text-muted-foreground">{module.description}</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-4">
                      <div className="space-y-4">
                        {content.map((item, itemIndex) => {
                          // Check if content should be rendered
                          const shouldRender = (() => {
                            if (item.content_type === 'interactive') {
                              const metadata = item.metadata as any;
                              const interactiveType = metadata?.interactiveType;
                              return !!interactiveType;
                            }
                            return !!(item.content_text || item.content_url || item.youtube_url || item.vimeo_url);
                          })();

                          if (!shouldRender) return null;

                          return (
                            <div key={item.id} className="space-y-3">
                              <div className="flex items-center gap-2">
                                {getContentIcon(item.content_type)}
                                <h4 className="font-medium">{item.title}</h4>
                                <div className="ml-auto">
                                  {completedContent.has(item.id) ? (
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <Circle className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </div>
                              </div>
                              <div className="pl-8">
                                {renderContent(item)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Reflection Section */}
                      <div className="space-y-4 border-t pt-4">
                        <Label htmlFor={`reflection-${moduleIndex}`}>Reflection Notes</Label>
                        <Textarea
                          id={`reflection-${moduleIndex}`}
                          placeholder="Document your learning, insights, and questions for this module..."
                          value={reflections[`module-${moduleIndex}`] || ''}
                          onChange={(e) => handleReflectionChange(`module-${moduleIndex}`, e.target.value)}
                          rows={4}
                        />
                        
                        <Button
                          variant="outline"
                          disabled={!reflections[`module-${moduleIndex}`]?.trim()}
                          className="w-full"
                        >
                          <Sparkles className="h-4 w-4 mr-2" />
                          Save Reflection
                        </Button>
                      </div>

                      <div className="flex space-x-2">
                        <Button 
                          onClick={() => {
                            // Mark all content in this module as complete
                            const newCompleted = new Set(completedContent);
                            content.forEach(item => newCompleted.add(item.id));
                            setCompletedContent(newCompleted);
                          }}
                          disabled={moduleCompleted}
                        >
                          {moduleCompleted ? (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Module Completed
                            </>
                          ) : (
                            'Complete Module'
                          )}
                        </Button>
                        {moduleIndex < modules.length - 1 && (
                          <Button 
                            variant="outline" 
                            onClick={() => setCurrentModuleIndex(moduleIndex + 1)}
                          >
                            Next Module
                            <ChevronRight className="h-4 w-4 ml-2" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </CardContent>
      </Card>

      {/* Course Completion */}
      {progress === 100 && totalModules > 0 && (
        <Card className="bg-green-50 border-green-200">
          <CardHeader>
            <CardTitle className="text-green-800 flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              Course Completed!
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-green-700">
              Congratulations! You have successfully completed all modules in this course. 
              Your learning journey has been documented in your reflections.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};